import { type AttributionContext, TrackingEventKey } from "@next-platform/packages-ts-types";
import {
  API_VERSION,
  getUserSettings,
  IAuthenticatedUser,
  setAuthUser,
  setInvalid,
  setIsUserSettingsLoaded,
  setProfile,
  useAppDispatch,
} from "@next-platform/client-common-core";

import { safeGetAttributionContext } from "@/features/attribution/safeGetAttributionContext";
import fireConversion from "@/features/tracking/registry/fireConversion";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import jsonApiService from "@/services/jsonApiService";
import useAuth from "@/services/useAuth";
import { typedLocalStorageService } from "@/utils/localStorageRe";

const ensureUserProfileLogger = appLogger.withTag("EnsureUserProfile");

/**
 * Minimum permission count required before we attempt to fetch/create
 * a user row. A brand-new Kinde token carries exactly one permission
 * (the "create user" grant); anything beyond that means the user row
 * already exists on our side.
 */
const MIN_PERMISSIONS_TO_LOAD_PROFILE = 1;

/**
 * Fire the signup browser conversion exactly at the first moment a
 * user row exists. `eventId = userId` matches the backend CAPI
 * producer (`emitCapiSignup`) so Meta dedups the browser Pixel hit
 * against the server-side event.
 *
 * Silent no-op when the user was not just created or when no pixel
 * is configured for the current app.
 */
function maybeFireSignupConversion(wasJustCreated: boolean, userId: string): void {
  if (!wasJustCreated) {
    return;
  }
  fireConversion({ eventKey: TrackingEventKey.Signup, eventId: userId });
}

/* TODO(karl): [NX-7080] revisit this eslint-disable — isolate the backend snake_case wire fields in a typed codec so callers can use camelCase without needing this block-level disable. */
/* eslint-disable @typescript-eslint/naming-convention -- `affiliate_id` and `tap_tracking_id` are wire fields on the backend `users` POST endpoint. Renaming them here would desynchronize the client from the server contract. */
type ExtendedUserPayload = IAuthenticatedUser & {
  sub?: string;
  affiliate_id?: string;
  tap_tracking_id?: string;
  attribution?: AttributionContext;
  // Present only when auth state was seeded from a JWT via `saveToken`
  // (partner login and the SpicyChat Android checkout handoff). Used solely
  // to pass the profile-load guard below; never sent as an API bearer.
  token?: string;
};
/* eslint-enable @typescript-eslint/naming-convention -- end of backend-contract snake_case block. */

type CreateUserResult = Awaited<ReturnType<typeof jsonApiService.createResource>>;
// `id` is tightened to required so `Existing` / `Created` outcomes
// prove at the type level that every id-scoped side effect (settings
// fetch, localStorage id, Signup conversion) has a stable identifier
// to work with. Any response lacking an id is funneled into
// `Invalid` at the runtime checks below.
type ResolvedUser = NonNullable<NonNullable<CreateUserResult>["user"]> & { id: string };

/**
 * Discriminant tags for the fetch-or-create handshake outcome.
 * Typed const (not string literals) so every branch — the producing
 * sites in `fetchOrCreate` and the consuming site in
 * `loadOrCreateUserProfile` — refers to the same identifier, making
 * renames safe and autocomplete work.
 *
 * - `Existing` → fetched an already-persisted user row; skip Signup.
 * - `Created`  → a new user row was just inserted; Signup must fire.
 * - `Invalid`  → fetch returned a response with no user AND the
 *                follow-up create also failed. The caller must
 *                dispatch `setInvalid(true)` so the UI routes the
 *                session to the onboarding/validation flow.
 *
 * Any OTHER failure (e.g. fetch threw and create also threw)
 * propagates as a thrown error so the caller's outer try/catch
 * routes it to `setIsUserSettingsLoaded(true)` and surfaces the
 * underlying transport error to logging.
 */
const FetchOrCreateOutcomeKind = {
  EXISTING: "existing",
  CREATED: "created",
  INVALID: "invalid",
} as const;
type FetchOrCreateOutcomeKind = (typeof FetchOrCreateOutcomeKind)[keyof typeof FetchOrCreateOutcomeKind];

type FetchOrCreateOutcome =
  | { kind: typeof FetchOrCreateOutcomeKind.EXISTING; user: ResolvedUser }
  | { kind: typeof FetchOrCreateOutcomeKind.CREATED; user: ResolvedUser }
  | { kind: typeof FetchOrCreateOutcomeKind.INVALID };

async function buildCreateUserPayload(authUser: ExtendedUserPayload): Promise<ExtendedUserPayload> {
  const payload: ExtendedUserPayload = { ...authUser };
  // These tracking ids are global (per-device, not per-user), so we
  // read them from localStorage rather than the auth payload.
  const affiliateId = localStorage.getItem("aff_id");
  const tapTrackingId = localStorage.getItem("tap_vid");

  if (affiliateId) {
    payload.affiliate_id = affiliateId;
  }
  if (tapTrackingId) {
    payload.tap_tracking_id = tapTrackingId;
  }

  // Attach the attribution envelope captured at landing time so the
  // backend can seed User.attribution on creation (first-touch +
  // last-touch + partners + Pixel cookies for downstream CAPI).
  // Non-blocking read — a failure must never abort user creation.
  const attribution = await safeGetAttributionContext();
  if (attribution !== undefined) {
    payload.attribution = attribution;
  }
  return payload;
}

async function createUser(authUser: ExtendedUserPayload): Promise<CreateUserResult> {
  const payload = await buildCreateUserPayload(authUser);
  return jsonApiService.createResource("users", payload as unknown as Record<string, unknown>, API_VERSION.V2);
}

async function fetchOrCreate(authUser: ExtendedUserPayload): Promise<FetchOrCreateOutcome> {
  // Narrow try: only fetch errors are swallowed here. A failed create
  // must propagate (or be handled by its own dedicated try below) so
  // we never issue more than one `POST /users` per call.
  let fetched: CreateUserResult | undefined;
  let didFetchThrow = false;
  try {
    fetched = await jsonApiService.fetchResource("users", null, {}, API_VERSION.V2);
  } catch (error) {
    ensureUserProfileLogger.warn("fetch user failed; will attempt create", { error });
    didFetchThrow = true;
  }

  if (fetched?.user?.id) {
    return { kind: FetchOrCreateOutcomeKind.EXISTING, user: fetched.user };
  }

  if (didFetchThrow) {
    // Fetch threw → escalate create errors to the caller's outer
    // catch so transport failures land in `setIsUserSettingsLoaded`
    // + logging rather than being silently absorbed.
    //
    // Require `created.user.id`: any create response without an id is
    // funneled to `kind: "invalid"`. This is enforced at both the
    // type level (`ResolvedUser & { id: string }`) and the runtime
    // guard below so the compiler flags any future caller trying to
    // dispatch id-scoped side effects without a stable identifier.
    const created = await createUser(authUser);
    if (!created?.user?.id) {
      return { kind: FetchOrCreateOutcomeKind.INVALID };
    }
    return { kind: FetchOrCreateOutcomeKind.CREATED, user: created.user };
  }

  // Fetch succeeded but the response had no user id. Treat a create
  // failure on THIS path as `invalid` (soft failure → onboarding
  // flow) rather than propagating, because fetch already succeeded
  // at the transport layer — the issue is profile state, not network.
  try {
    const created = await createUser(authUser);
    if (!created?.user?.id) {
      return { kind: FetchOrCreateOutcomeKind.INVALID };
    }
    return { kind: FetchOrCreateOutcomeKind.CREATED, user: created.user };
  } catch (error) {
    ensureUserProfileLogger.warn("create after empty fetch failed", { error });
    return { kind: FetchOrCreateOutcomeKind.INVALID };
  }
}

export default function useEnsureUserProfile() {
  const { permissions, getToken } = useAuth();
  const dispatch = useAppDispatch();

  const shouldLoadProfile = (authUser: ExtendedUserPayload): boolean => {
    if (!authUser?.id && !authUser?.sub) {
      return false;
    }
    return (permissions?.length ?? 0) > MIN_PERMISSIONS_TO_LOAD_PROFILE;
  };

  const loadOrCreateUserProfile = async (authUser: ExtendedUserPayload): Promise<void> => {
    if (!shouldLoadProfile(authUser)) {
      return;
    }
    try {
      // `getToken()` is undefined in the SpicyChat Android checkout WebView
      // (no Kinde session, and the handoff is applied via `saveToken`, not a
      // partner login). Fall back to the handoff token already on auth state
      // — NEVER re-read the URL. This value only passes the guard below; the
      // real API bearer comes from `authProvider.getToken()` / the native
      // WebView injection, never from here.
      const token = (await getToken()) || authUser.token;
      // Abort cleanly on no-token rather than letting an empty-string
      // token reach the API layer and cause a downstream 401 cascade.
      // `setInvalid(true)` routes the session to the onboarding flow,
      // matching the treatment of other auth-resolution failures.
      if (!token) {
        ensureUserProfileLogger.warn("no token available; aborting profile load");
        dispatch(setInvalid(true));
        return;
      }

      const outcome = await fetchOrCreate(authUser);
      if (outcome.kind === FetchOrCreateOutcomeKind.INVALID) {
        dispatch(setInvalid(true));
        return;
      }

      // `ResolvedUser & { id: string }` guarantees `outcome.user.id`
      // is present for `Existing` / `Created` outcomes, so every
      // id-scoped side effect below is safe without a runtime guard.
      void dispatch(getUserSettings());
      void typedLocalStorageService.setUserId(outcome.user.id);
      maybeFireSignupConversion(outcome.kind === FetchOrCreateOutcomeKind.CREATED, outcome.user.id);
      dispatch(setProfile(outcome.user));
    } catch (error) {
      ensureUserProfileLogger.warn("loadOrCreateUserProfile failed", { error });
      dispatch(setIsUserSettingsLoaded(true));
    }
  };

  const setAuthenticatedUser = (authUser: ExtendedUserPayload): void => {
    dispatch(setAuthUser(authUser));
    void loadOrCreateUserProfile(authUser);
  };

  return {
    setAuthenticatedUser,
  };
}
